'use client';
import { MagicLinkOperation } from '__generated__/graphql';
import { useMutation } from 'hooks/useQuery';
import {
  FormEvent,
  FormEventHandler,
  MouseEvent,
  MouseEventHandler,
  useCallback,
} from 'react';
import { trackingAuthButtonClick } from 'utils/tracking';
import { safeUrl } from 'utils/uri';
import { useClientContext } from '$/modules/context/client';
import { parseForm } from '$/modules/form';

export { useIdentity } from '@hyphengroup/cms-components/modules/auth';

export function useMagicLink(
  success: (data: { readonly email?: string }) => void,
  error: (e: unknown) => void,
): FormEventHandler<HTMLFormElement> {
  const { lang } = useClientContext();
  const magicLink = useMutation(MagicLinkOperation);
  return useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      const data = parseForm(event.currentTarget);
      const searchParams = new URLSearchParams(window.location.search);
      const refCode = searchParams.get('refcode');
      await magicLink
        .send({
          input: {
            email: String(data.email),
            subscribeToNewsletter: Boolean(data.consent),
            redirectURI: String(data.redirect_uri),
            language: lang.startsWith('zh') ? 'ZH' : 'EN',
            ...(refCode && { refCode }),
          },
        })
        .then(
          (data) => {
            if (data.data?.requestMagicLinkLogin?.email)
              success(data.data.requestMagicLinkLogin);
            else error(data.errors);
          },
          (e) => error(e),
        );
    },
    [magicLink, error, success, lang],
  );
}

export function useGoogleLogin(
  authEndpoint: string,
  customRedirectUri?: string,
  customTracking?: boolean,
): MouseEventHandler<HTMLButtonElement> {
  return useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      if (typeof window === 'undefined') {
        return;
      }

      const data = parseForm(event.currentTarget.form);

      if (!customTracking) {
        trackingAuthButtonClick(
          'Continue with Google',
          'google',
          `${document.title}_Continue with Google`,
          Boolean(data.consent),
        );
      }

      //Get referral code
      const urlParams = new URLSearchParams(window.location.search);
      const refCode = urlParams.get('refcode');

      const redirectUri =
        safeUrl(String(data.redirect_uri), window.location.href) ??
        new URL(window.location.href);
      const dest = new URL(`${authEndpoint}/login`, window.location.href);
      dest.searchParams.set('idp', 'google');
      dest.searchParams.set(
        'redirect_uri',
        customRedirectUri ? customRedirectUri : redirectUri.toString(),
      );
      dest.searchParams.set('consent', String(Boolean(data.consent)));
      if (refCode) {
        dest.searchParams.set('ref_code', refCode);
      }
      window.location.href = dest.toString();
    },
    [authEndpoint, customRedirectUri, customTracking],
  );
}
